/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import defaultImage from 'assets/svg/default-image-rectangle.svg'

import { IconClose, IconArrowDown } from 'components/Icons'
import ModalPortal from 'components/Modal/ModalPortal'
import { useOnClickOutside } from 'helpers/hooks'
import { getImageFileName } from 'helpers/common'

interface Props {
  photos?: string[] | null
  thumbnail?: string | null
}

import clsx from 'clsx'

export const StockKeepingUnitPreviewImage: React.FC<Props> = ({
  photos,
  thumbnail,
}: Props) => {
  const [showImage, setShowImage] = useState<string[]>()
  const divRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation(['sku'])

  const [isOpenPreview, setIsOpenPreview] = useState<boolean>(false)

  const base = 'f-stock-keeping-unit-preview-image'
  const photoThumbnail: any = thumbnail
  const photosList: any =
    photos?.filter((item, index: number) => index < 5) || []

  if (photoThumbnail) {
    photosList?.unshift(photoThumbnail)
  }
  while (photosList?.length > 5) {
    photosList?.pop()
  }

  const basePreview = 'preview-image'

  const handleIsOpenPreview = (order: number) => {
    setIsOpenPreview(true)
    setShowImage(photosList[order])
  }

  const handleClose = () => {
    setIsOpenPreview(false)
  }

  useOnClickOutside(divRef, handleClose)

  const modalPreview = () => {
    const renderLeftArrow = (order: number) => {
      return (
        order >= 0 && (
          <a
            data-testid='modal-arrow-left-icon'
            className={`${basePreview}__modal-arrow-left-icon ${basePreview}__model-bg-arrow-icon`}
            onClick={() => {
              setShowImage(photosList[order])
            }}
          >
            <IconArrowDown
              className={`icon--white ${basePreview}__rotate-arrow-left`}
              variants='sm'
            />
          </a>
        )
      )
    }

    const renderRightArrow = (order: number) => {
      return (
        order < photosList?.length && (
          <a
            data-testid='modal-arrow-right-icon'
            className={`${basePreview}__modal-arrow-right-icon ${basePreview}__model-bg-arrow-icon`}
            onClick={() => {
              setShowImage(photosList[order])
            }}
          >
            <IconArrowDown
              className={`icon--white ${basePreview}__rotate-arrow-right`}
              variants='sm'
            />
          </a>
        )
      )
    }

    const currentOrder = photosList?.indexOf(showImage)
    return (
      <div
        className={`${basePreview}__modal`}
        data-testid='preview-images-slide-modal'
      >
        <div className={`${basePreview}__modal-content carousel`} ref={divRef}>
          <label className={`${basePreview}__modal-title`}>
            {t('sku:imageSequence')}
            {currentOrder + 1}/{photosList?.length}
          </label>

          {renderLeftArrow(currentOrder - 1)}
          {renderRightArrow(currentOrder + 1)}

          <ol className='carousel__viewport'>
            {photosList?.map((item: string, index: number) => {
              const order = index + 1

              return (
                <div
                  className='carousel__slide'
                  key={`carousel__slide${order}`}
                  style={{
                    transform: `translateX(-${currentOrder * 100}%)`,
                  }}
                >
                  <div className='carousel__snapper'>
                    <div className={`${basePreview}__modal-content-image`}>
                      <div
                        className={`${basePreview}__modal-content-image-source`}
                      >
                        <img
                          className={clsx(
                            `${basePreview}__modal-thumbnail-source`,
                          )}
                          src={getImageFileName(item)}
                        />
                        <div
                          data-testid='preview-images-slide-modal-close-icon'
                          className={`${basePreview}__modal-close-icon`}
                          onClick={handleClose}
                        >
                          <IconClose
                            className={`icon--white ${basePreview}__modal-close-icon-size`}
                            variants='xs'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </ol>
        </div>
      </div>
    )
  }
  const mapPhotosList = () => {
    return photosList?.map((item: string, index: number) => {
      if (index === 0) {
        return (
          <div
            className={`${base}__image-thumbnail-layout`}
            onClick={() => handleIsOpenPreview(index)}
            key={`img-${index}`}
          >
            <img
              className={`${base}__image-thumbnail`}
              data-testid='preview-thumbnail'
              src={item ? getImageFileName(item) : defaultImage}
            />
          </div>
        )
      } else if (index > 0 && index < 4) {
        return (
          <img
            key={`img-${index}`}
            className={`${base}__image-row`}
            onClick={() => handleIsOpenPreview(index)}
            src={getImageFileName(item)}
          />
        )
      } else {
        return (
          <img
            key={`img-${index}`}
            className={`${base}__last-image-row`}
            onClick={() => handleIsOpenPreview(index)}
            src={getImageFileName(item)}
          />
        )
      }
    })
  }

  return (
    <div className={`${base}`} data-testid='preview-img-sku'>
      <div className={`${base}__product-image-header`}>
        <span className={`${base}__product-image-label`}>
          {t('sku:productImage.title')}
        </span>
        <span className={`${base}__product-image-count-label`}>
          {t('sku:productImage.count')}
          {photosList?.length}/5
        </span>
      </div>

      {photosList?.length > 0 ? (
        mapPhotosList()
      ) : (
        <img className={`${base}__image-thumbnail`} src={defaultImage} />
      )}
      {isOpenPreview && divRef ? (
        <ModalPortal>{modalPreview()}</ModalPortal>
      ) : null}
    </div>
  )
}

export default StockKeepingUnitPreviewImage
